import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronRight, ChevronLeft, Sparkles, Table, BarChart3, Image, Clock, MapPin, PenLine, Bot } from 'lucide-react';

// Tutorial step definitions
const TUTORIAL_STEPS = [
  {
    id: 'welcome',
    title: 'Welcome to AI Labs! 👋',
    description: 'A collection of AI tools for journalists.\nHere\'s a quick overview of each feature.',
    target: null,
    position: 'center',
    icon: Sparkles,
  },
  {
    id: 'text-tools',
    title: 'Text Tools',
    description: 'AI tools for article writing — proofreading, headline generation,\npress release conversion, editing, and foreign news translation.',
    target: '[data-tutorial="text-tools"]',
    position: 'bottom',
    icon: PenLine,
  },
  {
    id: 'visual-tools',
    title: 'Visual Tools',
    description: 'AI automatically generates tables, charts, infographics,\nimages, timelines, and maps.',
    target: '[data-tutorial="visual-tools"]',
    position: 'bottom',
    icon: BarChart3,
  },
  {
    id: 'template-gallery',
    title: 'Template Gallery',
    description: 'Click a visual tool to open the template gallery.\nSelect a style and the prompt is filled in automatically.',
    target: '[data-tutorial="visual-tools"]',
    position: 'bottom',
    icon: Image,
  },
  {
    id: 'input',
    title: 'Input Box',
    description: 'Type your request here.\nYou can also attach files, select tools, and switch AI models.',
    target: '[data-tutorial="input"]',
    position: 'top',
    icon: Bot,
  },
  {
    id: 'complete',
    title: 'All set! 🎉',
    description: 'You\'re ready to use AI Labs.\nFeel free to ask anything anytime!',
    target: null,
    position: 'center',
    icon: Sparkles,
  },
];

const TUTORIAL_COMPLETED_KEY = 'ai-labs-tutorial-completed';

const TutorialGuide = ({ onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [targetRect, setTargetRect] = useState(null);

  useEffect(() => {
    const completed = localStorage.getItem(TUTORIAL_COMPLETED_KEY);
    if (!completed) {
      const timer = setTimeout(() => setIsVisible(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  useEffect(() => {
    const step = TUTORIAL_STEPS[currentStep];
    if (step.target) {
      const element = document.querySelector(step.target);
      if (element) {
        const rect = element.getBoundingClientRect();
        setTargetRect({
          top: rect.top - 8,
          left: rect.left - 8,
          width: rect.width + 16,
          height: rect.height + 16,
        });
      } else {
        setTargetRect(null);
      }
    } else {
      setTargetRect(null);
    }
  }, [currentStep]);

  const handleNext = useCallback(() => {
    if (currentStep < TUTORIAL_STEPS.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      handleComplete();
    }
  }, [currentStep]);

  const handlePrev = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  }, [currentStep]);

  const handleComplete = useCallback(() => {
    localStorage.setItem(TUTORIAL_COMPLETED_KEY, 'true');
    setIsVisible(false);
    onComplete?.();
  }, [onComplete]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isVisible) return;
      if (e.key === 'ArrowRight' || e.key === 'Enter') {
        handleNext();
      } else if (e.key === 'ArrowLeft') {
        handlePrev();
      } else if (e.key === 'Escape') {
        handleComplete();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isVisible, handleNext, handlePrev, handleComplete]);

  if (!isVisible) return null;

  const step = TUTORIAL_STEPS[currentStep];
  const IconComponent = step.icon;
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === TUTORIAL_STEPS.length - 1;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[9999]"
      >
        {/* Overlay */}
        <div className="absolute inset-0 bg-black/70" onClick={handleComplete} />

        {/* Spotlight (when target exists) */}
        {targetRect && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="absolute rounded-xl"
            style={{
              top: targetRect.top,
              left: targetRect.left,
              width: targetRect.width,
              height: targetRect.height,
              boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.7)',
              border: '2px solid rgba(234, 88, 12, 0.5)',
              background: 'transparent',
            }}
          />
        )}

        {/* Guide card */}
        <motion.div
          key={currentStep}
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -20, scale: 0.95 }}
          transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
          className={`
            absolute bg-bg-100 rounded-2xl shadow-2xl border border-bg-300/50
            w-[90%] max-w-md p-6
            ${step.position === 'center' ? 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2' : ''}
            ${step.position === 'bottom' && targetRect ? 'left-1/2 -translate-x-1/2' : ''}
            ${step.position === 'top' && targetRect ? 'left-1/2 -translate-x-1/2' : ''}
          `}
          style={
            step.position === 'bottom' && targetRect
              ? { top: targetRect.top + targetRect.height + 20 }
              : step.position === 'top' && targetRect
              ? { bottom: window.innerHeight - targetRect.top + 20 }
              : {}
          }
        >
          {/* Close button */}
          <button
            onClick={handleComplete}
            className="absolute top-4 right-4 p-1 text-text-400 hover:text-text-100 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Icon */}
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-sedaily-500 to-orange-500 flex items-center justify-center mb-4">
            <IconComponent className="w-6 h-6 text-white" />
          </div>

          {/* Title */}
          <h3 className="text-lg font-bold text-text-100 mb-2">
            {step.title}
          </h3>

          {/* Description */}
          <p className="text-sm text-text-300 whitespace-pre-line leading-relaxed mb-6">
            {step.description}
          </p>

          {/* Progress + buttons */}
          <div className="flex items-center justify-between">
            {/* Progress dots */}
            <div className="flex gap-1.5">
              {TUTORIAL_STEPS.map((_, idx) => (
                <div
                  key={idx}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    idx === currentStep
                      ? 'bg-sedaily-500'
                      : idx < currentStep
                      ? 'bg-sedaily-500/50'
                      : 'bg-bg-300'
                  }`}
                />
              ))}
            </div>

            {/* Buttons */}
            <div className="flex gap-2">
              {!isFirstStep && (
                <button
                  onClick={handlePrev}
                  className="flex items-center gap-1 px-3 py-2 text-sm text-text-300 hover:text-text-100 transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Back
                </button>
              )}

              {isFirstStep && (
                <button
                  onClick={handleComplete}
                  className="px-3 py-2 text-sm text-text-400 hover:text-text-200 transition-colors"
                >
                  Skip
                </button>
              )}

              <button
                onClick={handleNext}
                className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-white bg-sedaily-500 hover:bg-sedaily-600 rounded-lg transition-colors"
              >
                {isLastStep ? 'Get Started' : 'Next'}
                {!isLastStep && <ChevronRight className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// Reset tutorial (for dev/testing)
export const resetTutorial = () => {
  localStorage.removeItem(TUTORIAL_COMPLETED_KEY);
  window.location.reload();
};

export default TutorialGuide;
